/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * Why the probe ended as it did.
 */
export const VerifyCimdURLResultOutcome = {
  Valid: "valid",
  InvalidUrl: "invalid_url",
  Unreachable: "unreachable",
  Unparseable: "unparseable",
  InvalidDocument: "invalid_document",
} as const;
/**
 * Why the probe ended as it did.
 */
export type VerifyCimdURLResultOutcome = ClosedEnum<
  typeof VerifyCimdURLResultOutcome
>;

/**
 * Outcome of probing a CIMD document URL. Nothing is persisted.
 */
export type VerifyCimdURLResult = {
  /**
   * The document's client_name, set only when verified. Lets an operator confirm the URL names the client they intended.
   */
  clientName?: string | undefined;
  /**
   * Human-readable explanation, safe to display to the operator.
   */
  detail: string;
  /**
   * The validated document rendered as JSON, set only when verified. Re-encoded from what Speakeasy parsed rather than echoed from the wire, so it shows what the authorization server will act on.
   */
  document?: string | undefined;
  /**
   * Status the document endpoint returned; omitted when no response was received.
   */
  httpStatus?: number | undefined;
  /**
   * Why the probe ended as it did.
   */
  outcome: VerifyCimdURLResultOutcome;
  /**
   * Stable machine label for the rule that rejected the document, e.g. client_id_mismatch. Set only for invalid_url and invalid_document.
   */
  reason?: string | undefined;
  /**
   * True when the document was fetched and passed every check the authorization server applies. A client presenting this URL will not be rejected for its document.
   */
  verified: boolean;
};

/** @internal */
export const VerifyCimdURLResultOutcome$inboundSchema: z.ZodMiniEnum<
  typeof VerifyCimdURLResultOutcome
> = z.enum(VerifyCimdURLResultOutcome);

/** @internal */
export const VerifyCimdURLResult$inboundSchema: z.ZodMiniType<
  VerifyCimdURLResult,
  unknown
> = z.pipe(
  z.object({
    client_name: z.optional(z.string()),
    detail: z.string(),
    document: z.optional(z.string()),
    http_status: z.optional(z.int()),
    outcome: VerifyCimdURLResultOutcome$inboundSchema,
    reason: z.optional(z.string()),
    verified: z.boolean(),
  }),
  z.transform((v) => {
    return remap$(v, {
      "client_name": "clientName",
      "http_status": "httpStatus",
    });
  }),
);

export function verifyCimdURLResultFromJSON(
  jsonString: string,
): SafeParseResult<VerifyCimdURLResult, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => VerifyCimdURLResult$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'VerifyCimdURLResult' from JSON`,
  );
}
